import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Activity, Save } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';

function EnergyTracker() {
  const [energyLevel, setEnergyLevel] = useState(50);
  const [appliedRemedies, setAppliedRemedies] = useState([]);
  const [notes, setNotes] = useState('');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await axios.get('/api/tracker?days=30');
      setLogs(response.data.logs || []);
    } catch (err) {
      console.error('Error fetching logs:', err);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      await axios.post('/api/tracker', {
        date: today,
        energy_level: energyLevel,
        applied_remedies: appliedRemedies,
        notes
      });
      fetchLogs();
      setAppliedRemedies([]);
      setNotes('');
      alert('Energy log saved!');
    } catch (err) {
      console.error('Error saving log:', err);
      alert('Failed to save log');
    } finally {
      setLoading(false);
    }
  };

  const commonRemedies = [
    'Meditation',
    'Exercise',
    'Journaling',
    'Color therapy',
    'Mantra practice',
    'Breathing exercises',
    'Nature walk',
    'Creative activity'
  ];

  const toggleRemedy = (remedy) => {
    if (appliedRemedies.includes(remedy)) {
      setAppliedRemedies(appliedRemedies.filter(r => r !== remedy));
    } else {
      setAppliedRemedies([...appliedRemedies, remedy]);
    }
  };

  // Prepare chart data
  const chartData = logs.slice(0, 14).reverse().map(log => ({
    date: format(new Date(log.date), 'MMM dd'),
    energy: log.energy_level
  }));

  return (
    <div className="page">
      <div className="container" style={{ maxWidth: '800px' }}>
        <button
          onClick={() => navigate('/dashboard')}
          className="btn btn-ghost"
          style={{ marginBottom: '2rem' }}
          data-testid="back-button"
        >
          <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} />
          Dashboard
        </button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>Energy Tracker</h1>
          <p style={{ color: '#9CA3AF', marginBottom: '2rem' }}>Track your daily energy and applied remedies</p>
        </motion.div>

        {/* Today's Log */}
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Activity size={20} color="#00D9FF" />
            Today's Energy
          </h3>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <label className="form-label">Energy Level</label>
              <span style={{ fontSize: '1.5rem', fontWeight: 700, color: '#00D9FF' }}>{energyLevel}</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={energyLevel}
              onChange={(e) => setEnergyLevel(parseInt(e.target.value))}
              data-testid="energy-slider"
              style={{
                width: '100%',
                height: '8px',
                borderRadius: '4px',
                background: `linear-gradient(to right, #10B981 0%, #10B981 ${energyLevel}%, #374151 ${energyLevel}%, #374151 100%)`
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#9CA3AF', marginTop: '0.25rem' }}>
              <span>Low</span>
              <span>Medium</span>
              <span>High</span>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Applied Remedies</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {commonRemedies.map(remedy => (
                <button
                  key={remedy}
                  onClick={() => toggleRemedy(remedy)}
                  className="badge"
                  data-testid={`remedy-${remedy}`}
                  style={{
                    cursor: 'pointer',
                    background: appliedRemedies.includes(remedy) ? 'rgba(0, 217, 255, 0.2)' : 'rgba(55, 65, 81, 0.5)',
                    color: appliedRemedies.includes(remedy) ? '#00D9FF' : '#9CA3AF',
                    border: appliedRemedies.includes(remedy) ? '1px solid #00D9FF' : '1px solid transparent',
                    padding: '0.5rem 1rem'
                  }}
                >
                  {remedy}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Notes (optional)</label>
            <textarea
              className="form-input"
              rows="3"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="How are you feeling today?"
              data-testid="notes-input"
              style={{ resize: 'vertical' }}
            />
          </div>

          <button
            onClick={handleSave}
            className="btn btn-primary"
            disabled={loading}
            data-testid="save-log"
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          >
            <Save size={20} />
            {loading ? 'Saving...' : 'Save Today\'s Log'}
          </button>
        </div>

        {/* Chart */}
        {chartData.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="card"
          >
            <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem' }}>Last 14 Days</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9CA3AF" style={{ fontSize: '0.75rem' }} />
                <YAxis stroke="#9CA3AF" domain={[0, 100]} />
                <Tooltip
                  contentStyle={{
                    background: '#1E2139',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: '#F9FAFB'
                  }}
                />
                <Line type="monotone" dataKey="energy" stroke="#00D9FF" strokeWidth={3} dot={{ fill: '#00D9FF', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>
        )}
      </div>
    </div>
  );
}

export default EnergyTracker;